import { Progress, Image, message as Message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import MessageStatus from '@/pages/messages/components/message-status';
import { useChatContext } from '@/contexts/chat/context';
import { safeJson } from '@/scripts/utils';
import { FileTypeIcon } from '@/configs/options/file';
import { formatFileSize } from '@/scripts/utils/file-analyzer';
import {
  fileTransferService,
  TransferState,
} from '@/contexts/chat/scripts/hooks/useFileMessages/file-sdk';
import {
  FileInfoStatus,
  type FileInfo,
} from '@/contexts/chat/scripts/hooks/useFileMessages/types';
import MessageExpireTime from '../messageExpireTime';
import type { MessageFileProps } from './interface';
import styles from './index.module.scss';
import DownloadIcon from './images/download.svg?react';

export default function MessageFile({
  className,
  isMe,
  message,
  chat,
  onClickStatus,
  displayTime,
}: MessageFileProps) {
  const {
    fileMessages: { map, downloadFile },
    abortFileMessage,
  } = useChatContext();
  const [objectURL, setObjectURL] = useState('');
  const [percent, setPercent] = useState(0);

  const fm = useMemo(() => map[message.cli_msg_id], [map, message.cli_msg_id]);

  const info = useMemo(
    () => safeJson(message.payload, {} as FileInfo),
    [message.payload]
  );
  // TODO
  const hasInfo = false;

  const operations = useMemo(() => {
    return (
      <div className={styles.operations}>
        {hasInfo && <div className={styles.left}></div>}
        <div className={styles.right}>
          <MessageExpireTime message={message} chat={chat} displayTime={displayTime} />
          <MessageStatus chat={chat} message={message} onClick={onClickStatus} />
        </div>
      </div>
    );
  }, [chat, displayTime, hasInfo, message, onClickStatus]);

  const onVisibleChange = useCallback(
    async (visible: boolean) => {
      if (visible) {
        if (objectURL) return;
        if (fm && fm.file) {
          setObjectURL(URL.createObjectURL(fm.file));
        } else if (info.meta) {
          setPercent(0);
          try {
            const result = await fileTransferService.download(JSON.parse(info.meta), {
              onProgress: (progress) => {
                setPercent(Math.round(progress));
              },
            });
            if (result.data) {
              setObjectURL(URL.createObjectURL(result.data));
            }
          } catch (error) {
            console.error(error);
            Message.error('Failed to download file');
          }
        }

        setPercent(100);
      }
    },
    [fm, info.meta, objectURL]
  );

  useEffect(() => {
    return () => {
      if (objectURL) {
        setObjectURL('');
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [objectURL]);

  return (
    <div className={cls(styles.messageFile, className)}>
      <div
        className={cls(
          styles.fileWrapper,
          isMe && styles.isMe,
          hasInfo && styles.hasInfo
        )}
      >
        <div className={cls(styles.file, info.thumbnail && styles.imgFile)}>
          {info.thumbnail ? (
            <Image
              wrapperClassName={styles.previewImage}
              className={styles.image}
              preview={{
                mask: <EyeOutlined />,
                src: objectURL,
                onVisibleChange,
                imageRender(originalNode) {
                  return (
                    <>
                      {originalNode}
                      {percent < 100 && (
                        <Progress
                          className={styles.spinProgress}
                          percent={percent}
                          strokeColor="#fff"
                          size="small"
                          type="circle"
                        />
                      )}
                    </>
                  );
                },
              }}
              fallback={info.thumbnail}
              src={info.thumbnail}
            />
          ) : (
            <FileTypeIcon extType={info.ext} src={info.thumbnail} />
          )}

          <div className={styles.fileContent}>
            <div className={styles.fileName}>{info.name}</div>
            <div className={styles.fileOperations}>
              <div className={styles.fileInfo}>
                <div>{formatFileSize(info.size)}</div>
                {fm?.status && <div>{fm?.status}</div>}
                {[
                  TransferState.UPLOADING,
                  TransferState.ENCRYPTING,
                  TransferState.DECRYPTING,
                  TransferState.DOWNLOADING,
                ].includes(fm?.status) && (
                  <CloseOutlined
                    className={styles.close}
                    onClick={() => {
                      abortFileMessage({ chat_id: chat.chat_id, msg: message });
                    }}
                  />
                )}
              </div>
              {info.meta
                ? operations
                : fm
                  ? null
                  : info.status && (
                      <span
                        style={{
                          color:
                            info.status === FileInfoStatus.ERROR
                              ? 'var(--color-error)'
                              : '',
                        }}
                      >
                        {FileInfoStatus[info.status]}
                      </span>
                    )}
            </div>
          </div>
        </div>
        {hasInfo && operations}

        {fm && (
          <Progress
            className={styles.progress}
            strokeWidth={4}
            percent={fm.progress * 100}
            showInfo={false}
          />
        )}
      </div>
      {!isMe && info.meta && (
        <DownloadIcon
          className="pointer"
          onClick={() =>
            downloadFile({
              id: message.cli_msg_id,
              info: safeJson(message.payload, {} as any),
            })
          }
        />
      )}
    </div>
  );
}
